import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AiSourcingService } from 'src/app/_services/ai-sourcing.service';
import { ClickOutsideDirective } from 'src/app/directives/click-outside.directive';
import { HireSearchService } from '../services/hire-search.service';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { RoleFitDataService } from '../services/role-file-data.service';
import { PROJECT_TAB_CONSTANTS } from 'src/app/projects/constant/projectConstants';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CandidateSelectionService } from 'src/app/projects/services/candidate-selection.service';

@Component({
  selector: 'app-add-to-pipeline',
  imports: [ReactiveFormsModule, ClickOutsideDirective, TitleCasePipe, NgbAlertModule, CommonModule, MatTooltipModule],
  templateUrl: './add-to-pipeline.component.html',
  styleUrl: './add-to-pipeline.component.scss',
})
export class AddToPipelineComponent implements OnInit, OnChanges, OnDestroy {
  pipelineSearchControl: FormControl = new FormControl('');
  filteredPipelines: any[] = [];
  @Input() projectList: any[] = []; // Input property to receive project list
  isDropdownOpen = false;
  @Input() candidateid = ''; // Input property to receive candidate data
  successMessage: string = ''; // Property to hold the success message
  errorMessage: string = ''; // Property to hold the error message
  @Input() selectedCandidates: any[] = [];
  @Input() type = ''; // Input property to receive the type (single or bulk)
  @Input() calledFrom = ''; // Input property to receive the source of the call
  projectnumber = '';
  @Output() pipelineAdded = new EventEmitter<any>();
  @Input() criteriaChips: any[] = []; // Input property to receive criteria chips
  @Input() candidatedetails: any; // Input property to receive candidate details
  @Input() searchType = ''; // Input property to receive search type
  /**CandidateSelectionService
   * Optional. Passed only by bulk-mode call sites (hire-candidate-list) — some
   * selected candidates may never have rendered a card, so RoleFitDataService has
   * nothing for them. If provided, called with selectedCandidates before submit
   * to backfill missing entries. Single-candidate mode doesn't need this since
   * the card that's currently open has already populated its own data.
   */
  @Input() ensureRoleFitData?: (ids: string[]) => Observable<void>;
  @Input() referenceElasticId: string | null = null;
  @Input() referenceCandidateData: any = null;
  isAddToPipelineLoading: boolean = false;
  private destroy$ = new Subject<void>();
  /** candidate-status API in flight - a skeleton is shown in place of the button until it responds (or fails). */
  isPipelineStatusLoading = false;
  /** projectnumber -> true when the candidate (or, in bulk, every selected candidate) is already in it */
  pipelineStatusByProject: Record<string, boolean> = {};
  private pipelineStatusSubscription?: Subscription;
  /** Minimum time the skeleton stays up, so a fast response doesn't flash it for a frame or two. */
  private static readonly MIN_SKELETON_MS = 300;
  private pipelineStatusLoadStartedAt = 0;
  private pipelineStatusApplyTimer?: ReturnType<typeof setTimeout>;
  private pipelineStatusRequestKey = '';
  private isInitialized = false;
  private pipelineStatusCandidateIds: string[] = [];
  private pipelineStatusIdType: 'elastic_id' | 'candidatenumber' = 'elastic_id';
redirectSource: string | null = null;
  constructor(private hireService: HireSearchService, private roleFitDataService: RoleFitDataService, private route: ActivatedRoute, private candidateSelectionService: CandidateSelectionService) {
    this.filteredPipelines = this.projectList; // Initialize with the full list
  }

  ngOnInit() {
    this.projectnumber = window.location.pathname?.split('/')[2];
    this.redirectSource = this.route.snapshot.queryParams['redirectSource'] || null;

    this.pipelineSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => this.hireService.getProjectTypeahead((query ?? '').toLowerCase())),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (response.success == true) {
        this.filteredPipelines = response.data.length === 0 ? this.projectList : response.data;
      }
    });
    this.isInitialized = true;
    this.refreshPipelineStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isInitialized && (changes['candidateid'] || changes['candidatedetails'] || changes['calledFrom'] || changes['selectedCandidates'] || changes['type'])) {
      this.refreshPipelineStatus();
    }
  }


  private refreshPipelineStatus(): void {
    const isAtsCandidate = this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_UPLOAD_CANDIDATES || this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_INBOUND_APPLICATION;
    // Bulk upload/inbound selections are candidate objects; every other bulk caller passes plain ids.
    const ids = this.type === 'bulk'
      ? (this.selectedCandidates ?? []).map((c: any) => String((typeof c === 'string' ? c : c?.candidatenumber) || ''))
      : [String(isAtsCandidate ? this.candidatedetails?.candidatenumber || '' : this.candidateid || '')];
    // Upload/Inbound land in candidate.elastic_id too (see candidateHelper.createCandidatesInBulk),
    // so only 'my-candidates' - which carries candidate.candidatenumber as candidateid - needs the
    // other column. Telling the backend which column to search avoids an OR across both.
    this.pipelineStatusIdType = this.calledFrom === 'my-candidates' ? 'candidatenumber' : 'elastic_id';

    // De-dupe on content, not reference: some bulk callers bind selectedCandidates
    // to a getter that returns a new array every change-detection cycle.
    const candidateIds = [...new Set(ids.filter(Boolean))].sort();
    const requestKey = `${this.pipelineStatusIdType}:${candidateIds.join(',')}`;
    if (requestKey === this.pipelineStatusRequestKey) return;
    this.pipelineStatusRequestKey = requestKey;
    this.pipelineStatusCandidateIds = candidateIds;
    this.pipelineStatusSubscription?.unsubscribe();
    clearTimeout(this.pipelineStatusApplyTimer);
    this.pipelineStatusByProject = {};
    this.loadPipelineStatuses();
  }

  private loadPipelineStatuses(): void {
    const candidateIds = this.pipelineStatusCandidateIds;
    if (!candidateIds.length) {
      this.isPipelineStatusLoading = false;
      return;
    }
    this.isPipelineStatusLoading = true;
    this.pipelineStatusLoadStartedAt = Date.now();
    this.pipelineStatusSubscription = this.hireService.getCandidatePipelineStatus(candidateIds, this.pipelineStatusIdType).subscribe({
      next: status => {
        // A project only counts as "already in pipeline" when every candidate is in it -
        // for a single card that's just the one candidate, for bulk it's the whole selection.
        const [firstId, ...otherIds] = candidateIds;
        const byProject: Record<string, boolean> = {};
        Object.entries(status?.[firstId] || {}).forEach(([projectnumber, inPipeline]) => {
          if (inPipeline === true && otherIds.every(id => status?.[id]?.[projectnumber] === true)) {
            byProject[projectnumber] = true;
          }
        });
        this.finishPipelineStatusLoading(byProject);
      },
      // API failed: drop the skeleton and show the button (enabled). The request key is
      // kept on purpose - clearing it would make getter-bound bulk callers (new array
      // every change-detection cycle) retry the failing request in a loop.
      error: () => this.finishPipelineStatusLoading({})
    });
  }

  /** Swaps the skeleton for the button (in its final enabled/disabled state), after at least MIN_SKELETON_MS. */
  private finishPipelineStatusLoading(byProject: Record<string, boolean>): void {
    const apply = () => {
      this.pipelineStatusByProject = byProject;
      this.isPipelineStatusLoading = false;
    };
    const remaining = AddToPipelineComponent.MIN_SKELETON_MS - (Date.now() - this.pipelineStatusLoadStartedAt);
    clearTimeout(this.pipelineStatusApplyTimer);
    if (remaining > 0) {
      this.pipelineStatusApplyTimer = setTimeout(apply, remaining);
    } else {
      apply();
    }
  }

  isCandidateInPipeline(projectnumber: string): boolean {
    return this.pipelineStatusByProject[String(projectnumber)] === true;
  }

  canAddToProject(projectnumber: string): boolean {
    return !this.isAddToPipelineLoading && !this.isCandidateInPipeline(projectnumber);
  }

  get alreadyInPipelineTooltip(): string {
    return this.type === 'bulk' ? 'All selected candidates are already in this pipeline' : 'Candidate already in this pipeline';
  }


  filterPipeline(event: Event) {
    console.log('pipeline chips', this.criteriaChips);
    const query = (event.target as HTMLInputElement).value.toLowerCase();
    this.hireService.getProjectTypeahead(query)
      .subscribe((response: any) => {
        if (response.success == true) {
          if (response.data.length === 0) {
            this.filteredPipelines = this.projectList;
          } else {
            this.filteredPipelines = response.data;
          }
          // pipelineStatusByProject already covers every accessible project
          // for this candidate, so filtering the dropdown doesn't need a refetch.
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.pipelineStatusSubscription?.unsubscribe();
    clearTimeout(this.pipelineStatusApplyTimer);
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.pipelineSearchControl.setValue('', { emitEvent: false });
      this.filteredPipelines = [...this.projectList];
    }
  }

  closeDropdown() {
    this.isDropdownOpen = false;
  }

  addToPipeline(pipeline?: any) {
    if (this.isCandidateInPipeline(pipeline?.projectnumber || this.projectnumber)) return;
    if (this.type === 'bulk' && this.ensureRoleFitData) {
      this.ensureRoleFitData(this.selectedCandidates ?? []).subscribe({
        next: () => {
            this.submitPipelinePayload(pipeline)
        },
        // Best-effort: if the backfill fetch itself fails, still submit with
        // whatever data is already available rather than blocking the user.
        error: () => {
            this.submitPipelinePayload(pipeline)
        }
      });
    } else if(this.calledFrom === 'my-candidates'){
      this.submitPipelineForMyCandidates(pipeline);
    } else {
      this.submitPipelinePayload(pipeline);
    }
  }


/**
 * Submits the pipeline payload for adding candidates to the pipeline.
 * @param pipeline
 */
  private submitPipelinePayload(pipeline?: any) {
    let screeningResponse: any[] = [];
    let projectnumber: string = '';
    let elasticids: any[] = [];
    let source: string = '';

    this.isAddToPipelineLoading = true;
    const isSimilaritySearch = this.searchType?.includes('similar-candidate');

    if (!isSimilaritySearch) {
      if (this.type === 'bulk') {
        if (this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_UPLOAD_CANDIDATES || this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_INBOUND_APPLICATION) {
          this.selectedCandidates.forEach((candidate: any) => {
            if (candidate?.screening_response?.screening_criteria?.length > 0) {
              screeningResponse.push({
                elasticid: candidate.elasticid,
                revaluationrequired: candidate.revaluationrequired ?? 0,
                overallscore: candidate.screening_response?.overallscore ?? null,
                screeningcriteriajson: JSON.stringify(candidate.screening_response?.screening_criteria ?? null)
              });
            } else {
              screeningResponse = [];
            };
          });
        } else {
          (this.selectedCandidates ?? []).forEach((id: string) => {
            if(this.roleFitDataService?.getRoleFitData(id)?.length > 0) {
          screeningResponse.push({
              elasticid: id,
              overallscore: this.roleFitDataService.getOverallScore(id),
              screeningcriteriajson: JSON.stringify(this.roleFitDataService.getRoleFitData(id))
            });
            } else {
              screeningResponse = [];
            }

          });
        }
      } else {
        if (this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_UPLOAD_CANDIDATES || this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_INBOUND_APPLICATION) {
          if (this.candidatedetails?.screening_response?.screening_criteria?.length > 0) {
            screeningResponse.push({
              elasticid: this.candidatedetails?.candidatenumber,
              revaluationrequired: this.candidatedetails?.revaluationrequired ?? 0,
              overallscore: this.candidatedetails?.screening_response?.overallscore ?? null,
              screeningcriteriajson: JSON.stringify(this.candidatedetails?.screening_response?.screening_criteria ?? null)
            });
          } else {
            screeningResponse = [];
          };
        } else {
          if(this.roleFitDataService.getRoleFitData(this.candidateid)?.length > 0) {
      screeningResponse.push({
            elasticid: this.candidateid,
            overallscore: this.candidatedetails?.personSearchScore?.overall_screening_score ?? null,
            screeningcriteriajson: JSON.stringify(this.roleFitDataService.getRoleFitData(this.candidateid))
          });
          } else {
            screeningResponse = [];
          }

        };
      };
    };
    /**Seggregate Project Number */
    projectnumber = pipeline?.projectnumber ? pipeline.projectnumber : this.projectnumber;

    /** Seggregate Elastic IDs */
    if (this.type == 'bulk') {
      elasticids = this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_UPLOAD_CANDIDATES || this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_INBOUND_APPLICATION ? this.selectedCandidates.map((c: any) => c.candidatenumber) : this.selectedCandidates;
    } else {
      elasticids = this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_UPLOAD_CANDIDATES || this.calledFrom === PROJECT_TAB_CONSTANTS.PROJECT_INBOUND_APPLICATION ? [this.candidatedetails?.candidatenumber] : [this.candidateid];
    };

    /** Seggregate Source */
    source = this.calledFrom == PROJECT_TAB_CONSTANTS.PROJECT_UPLOAD_CANDIDATES ? "UPLOAD": this.calledFrom == PROJECT_TAB_CONSTANTS.PROJECT_INBOUND_APPLICATION ? "INBOUND" : isSimilaritySearch ? "SIMILARITY_SEARCH" : this.calledFrom == 'project-details-view' ? "SEARCH" : this.calledFrom == 'project-details-recommend-view' ? "WEBBTREE_RECOMMENDATION" : this.calledFrom == 'project-details-upload-view' ? "UPLOAD" : this.searchType == 'nl-search' ? 'NL_SEARCH' : this.searchType == 'job-description' ? 'JD_SEARCH' : this.searchType == 'advanced-search' ? 'ADVANCED_SEARCH' : this.calledFrom == 'my-candidates' ? 'MY_CANDIDATES' :this.redirectSource == 'talent-insights' || this.redirectSource == 'company-insights' ? 'INSIGHTS_SEARCH' : 'SEARCH';

    const payload: any = {
      projectnumber: projectnumber,
      elasticids: elasticids,
      screeningDetails: screeningResponse,
      source: source
    };

    if (isSimilaritySearch) {
      payload.candidateCompareData = this.buildCandidateCompareData();
      delete payload.screeningDetails;
    }

   if(payload['screeningDetails']?.length == 0 || this.calledFrom == 'project-details-recommend-view') {
    delete payload['screeningDetails'];
   }

   if(this.calledFrom == 'project-details-view' || this.calledFrom == 'project-details-search-view') {
    payload['sourceprojectnumber'] = window.location.pathname?.split('/')[2];
   }

this.hireService.addtoPipeline(payload).subscribe(
      (response: any) => {
        this.isAddToPipelineLoading = false;
        if (response.success == true) {
          this.recordPipelineAddition(projectnumber);
          if(response.message == 'All candidates are already present in the talent pool') {
            this.successMessage = 'All candidates are already present in the Pipeline';
          setTimeout(() => {
            this.successMessage = '';
                      this.closeDropdown();

          }, 3000); // Clear message after 3 seconds
         this.pipelineAdded.emit({
            success: true,
            message: 'All candidates are already present in the Pipeline'
          });

          } else {
         this.successMessage = 'Candidate added to pipeline successfully!';
          setTimeout(() => {
            this.successMessage = '';
          }, 3000); // Clear message after 3 seconds
          this.pipelineAdded.emit({
            success: true,
            message: 'Candidate added to pipeline successfully!'
          });

          // Optimistically increment the pipeline sidebar count
          this.candidateSelectionService.incrementPipelineCandidatesCount(elasticids.length);

          this.closeDropdown();
          }

          // Handle success response
        }
      },
      (error) => {
        this.isAddToPipelineLoading = false;

        this.errorMessage = 'Failed to add candidate to pipeline. Please try again.';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
        this.closeDropdown();
        // Handle error response
      }
    );
  }

  /**
   * A success response (incl. the backend's "already present" short-circuit) means every
   * submitted candidate is now in that project, so mark it directly - re-calling the
   * status API here would flash the skeleton again right after the add.
   */
  private recordPipelineAddition(projectnumber: string): void {
    if (!projectnumber) return;
    this.pipelineStatusByProject = { ...this.pipelineStatusByProject, [String(projectnumber)]: true };
  }

  submitPipelineForMyCandidates(pipeline: any) {
    let projectnumber: string = pipeline.projectnumber;
    let elasticids: any[] = [];
    if(this.type == 'bulk') {
      elasticids = this.selectedCandidates;
    } else {
      elasticids = [this.candidateid];
    }

    const payload: any = {
      projectnumber: projectnumber,
      candidatenumbers: elasticids,
      source: "MY_CANDIDATES"
    };

    this.hireService.addtoPipelineMyCandidates(payload).subscribe(
      (response: any) => {
        this.isAddToPipelineLoading = false;
        if (response.success == true) {
          this.recordPipelineAddition(projectnumber);
          if(response.message == 'All candidates are already present in the talent pool') {
            this.successMessage = 'All candidates are already present in the Pipeline';
          setTimeout(() => {
            this.successMessage = '';
            this.closeDropdown();

          }, 3000); // Clear message after 3 seconds
         this.pipelineAdded.emit({
            success: true,
            message: 'All candidates are already present in the Pipeline'
          });

          } else {
         this.successMessage = 'Candidate added to pipeline successfully!';
          setTimeout(() => {
            this.successMessage = '';
          }, 3000); // Clear message after 3 seconds
          this.pipelineAdded.emit({
            success: true,
            message: 'Candidate added to pipeline successfully!'
          });

          // Optimistically increment the pipeline sidebar count
          this.candidateSelectionService.incrementPipelineCandidatesCount(elasticids.length);

          this.closeDropdown();
          }

          // Handle success response
        }
      },
      (error) => {
        this.isAddToPipelineLoading = false;

        this.errorMessage = 'Failed to add candidate to pipeline. Please try again.';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
        this.closeDropdown();
        // Handle error response
      }
    );

  }


  /**
   * Builds one candidateCompareData entry per candidate for the similarity-search
   * (ideal candidates) flow. Single mode reads straight off candidatedetails since
   * that candidate's card has always already rendered and enriched it; bulk mode reads
   * from RoleFitDataService, which addToPipeline() backfills via ensureRoleFitData
   * before this ever runs, so even never-rendered selections have data.
   */
  private buildCandidateCompareData(): any[] {
    const ids = this.type === 'bulk' ? (this.selectedCandidates ?? []) : [this.candidateid];

    return ids.map((id: string) => {
      const rawMatch =
        (this.type !== 'bulk' && this.candidatedetails?.idealCandidateMatch)
          ? this.candidatedetails.idealCandidateMatch
          : this.roleFitDataService.getIdealCandidateMatch(id);

      const { elastic_id, ...matchFields } = rawMatch ?? {};

      return {
        candidateElasticId: id,
        referenceElasticId: this.referenceElasticId ?? null,
        referenceCandidateData: this.referenceCandidateData ?? {},
        ...matchFields
      };
    });
  }

  /**
   * Formats the screening response for adding candidates to the pipeline.
   * @param candidates
   * @returns
   */
  formatScreeningResponseForAddToPipeline(candidates: any): any[] {
    return {
      ...candidates,
      elasticid: candidates.candidatenumber,
      overallscore: candidates.overall_screening_score,
      screeningcriteriajson: candidates.screening_response?.screening_criteria
      // screeningcriteriajson: JSON.stringify({ candidatenumber: candidates.candidatenumber, screeningcriteriajson: candidates.screening_response?.screening_criteria })
    };
  };

}
